import { kDefaultLogoPath, kHaikuLogoPath, kHaikuFaviconPath, kDefaultFaviconPath } from '@/constants/constants-ui'
import { BrandEnum } from '@/firebase/auth/auth-jwt-schema'
import { UserCredential, OAuthCredential, AuthCredential } from 'firebase/auth'
import onboardBrandOAuth from '@/organizations/fetch/onboard-brand-sso'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { Dispatch, AnyAction } from '@reduxjs/toolkit'
import { GlobalToastType, kToastDurationShortMs } from '@/constants/constants-ui'
import { useSearchParams } from 'react-router-dom'

const haikuTenant = import.meta.env.VITE_DOMAIN_HAIKU_ENTRA_TENANT
const isPaxtonList = [BrandEnum.NONE, BrandEnum.PAXTON]
export const brandedAIFriendlyName = getBrandName()
export const brandedAIName = getBrandName(true)

function isLocalHost(host: string) {
  const normalizedHost = host.toLowerCase()
  return (
    normalizedHost === 'localhost' ||
    normalizedHost.startsWith('localhost:') ||
    normalizedHost === '127.0.0.1' ||
    normalizedHost.startsWith('127.0.0.1:') ||
    normalizedHost === '0.0.0.0' ||
    normalizedHost.startsWith('0.0.0.0:') ||
    normalizedHost === '[::1]' ||
    normalizedHost.startsWith('[::1]:')
  )
}

function getLocalBrandOverride(host: string): BrandEnum | null {
  if (import.meta.env.MODE === 'production' || !isLocalHost(host)) return null

  const localBrand = import.meta.env.VITE_LOCAL_BRAND?.trim().toLowerCase()
  if (localBrand === BrandEnum.HAIKU.toLowerCase()) return BrandEnum.HAIKU
  if (localBrand === BrandEnum.PAXTON.toLowerCase()) return BrandEnum.PAXTON
  return null
}

export function getFaviconFromBrand(brand: BrandEnum | null = null) {
  if (!brand || brand === BrandEnum.NONE) {
    brand = getBrandFromHost()
  }
  if (brand === BrandEnum.HAIKU) {
    return kHaikuFaviconPath
  }
  return kDefaultFaviconPath
}

export function getBrandFromHost(host = window.location.host) {
  const localBrandOverride = getLocalBrandOverride(host)
  if (localBrandOverride) return localBrandOverride

  if (host.includes('haiku')) {
    return BrandEnum.HAIKU
  }
  return BrandEnum.PAXTON
}

export function getLogoFromBrand(brand: BrandEnum | null = null) {
  if (!brand || brand === BrandEnum.NONE) {
    brand = getBrandFromHost()
  }
  if (brand === BrandEnum.HAIKU) {
    return kHaikuLogoPath
  }
  return kDefaultLogoPath
}

export function getBrandName(ai = false) {
  const brand = getBrandFromHost()
  if (brand === BrandEnum.HAIKU) {
    return 'Haiku'
  }
  return ai ? 'Paxton AI' : 'Paxton'
}

/**
 * Determines if user is from Paxton.
 * @param brand If null, will use the brand from the host
 * @returns
 */
export function isPaxtonBrand(brand: BrandEnum | null = null) {
  if (!brand || brand === BrandEnum.NONE) {
    brand = getBrandFromHost()
  }
  return isPaxtonList.includes(brand)
}

/**
 * Checks the current host to determine if a Microsoft tenant ID is needed.
 * @returns Microsoft tenant ID to restrict who can login to a URL
 */
export function getMicrosoftTenantId() {
  const hostBrand = getBrandFromHost()

  if (hostBrand === BrandEnum.HAIKU) {
    return haikuTenant
  }

  return 'common'
}

export function isTenantSSO() {
  return getMicrosoftTenantId() !== 'common'
}

export async function validateSSOCredentials(credential: UserCredential | OAuthCredential | AuthCredential, dispatch: Dispatch<AnyAction> | null = null) {
  let valid = true
  if (credential?.providerId === 'saml.haiku' && isTenantSSO()) {
    valid = await onboardBrandOAuth()
  }

  if (!valid) {
    const authFailed = 'Authentication Failed, Please try again.'
    if (dispatch != null) {
      dispatch(openGlobalToast({ type: GlobalToastType.ERROR, message: authFailed, durationMs: kToastDurationShortMs }))
    }
    throw new Error(authFailed)
  }
}

export function isHaikuHost() {
  return getBrandFromHost() === BrandEnum.HAIKU
}

/**
 * Generic function to check if the URL has an override query param set to 'paxton'
 * @returns boolean
 */
export function useOverridePaxton() {
  const [searchParams] = useSearchParams()
  return searchParams.get('override') === 'paxton'
}

export function getHelpCenterUrl(suffix: string = '') {
  if (isHaikuHost()) {
    return 'https://help.hkhaiku.com/'
  }
  return 'https://help.paxton.ai/help' + (suffix !== '' ? '/' + suffix : '')
}
